/**
 * useInfiniteMessages - 消息列表分页查询 Hook
 *
 * 使用 cursor 分页从 API 获取消息，支持上拉加载更多历史消息。
 * 消息以 desc（从新到旧）顺序返回，客户端需要反转为 asc 顺序用于 UI 显示。
 */
import { useMemo } from 'react'
import {
  infiniteQueryOptions,
  useInfiniteQuery,
  type QueryClient,
} from '@tanstack/react-query'
import {
  getEnginePlaySessionsSessionIdMessages,
  type GetEnginePlaySessionsSessionIdMessagesParams,
  type HandlersGetMessagesCursor,
} from '@/types/origin-point-engine'
import { logger } from '@/lib/logger'

/** 分页查询参数 */
export interface MessagesQueryParams {
  /** 每页获取数量 */
  limit?: number
  /** 是否包含 session 信息（仅首页） */
  includeSession?: boolean
}

/** 默认配置 */
const DEFAULT_LIMIT = 20

/**
 * 消息分页查询 Options
 */
export const messagesQueryOptions = (
  sessionId: string,
  params: MessagesQueryParams = {}
) => {
  const { limit = DEFAULT_LIMIT } = params

  return infiniteQueryOptions({
    // limit 影响 queryFn 的请求结果，需纳入 queryKey 以保证缓存正确区分
    queryKey: ['messages', sessionId, limit],
    queryFn: async ({ pageParam }) => {
      logger.debug('[useInfiniteMessages] 请求消息', {
        sessionId,
        cursor: pageParam,
        limit,
      })

      const queryParams: GetEnginePlaySessionsSessionIdMessagesParams = {
        limit,
        order: 'desc', // 从新到旧获取，客户端反转为从旧到新
        includeSession: pageParam === undefined, // 首页获取 session
        // 仅非首页传递 cursor（使用响应中的 nextCursor 值）
        ...(pageParam ? { cursor: pageParam } : {}),
      }

      const res = await getEnginePlaySessionsSessionIdMessages(
        sessionId,
        queryParams
      )

      logger.debug('[useInfiniteMessages] 响应', {
        hasMore: res.data?.hasMore,
        messageCount: res.data?.messages?.length,
      })

      return res.data
    },
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage): string | undefined => {
      if (!lastPage?.hasMore) return undefined
      // nextCursor 直接透传，无需编码
      return encodeCursor(lastPage.nextCursor)
    },
  })
}

/**
 * 将游标对象编码为字符串
 * 如果 API 返回的是对象，需要序列化；如果已经是字符串则直接返回
 */
function encodeCursor(
  cursor: HandlersGetMessagesCursor | string | undefined
): string | undefined {
  if (!cursor) return undefined
  if (typeof cursor === 'string') return cursor
  // 如果是对象，序列化为 JSON 字符串再 Base64 编码
  return btoa(JSON.stringify(cursor))
}

export interface UseInfiniteMessagesOptions {
  /** 是否启用查询 */
  enabled?: boolean
  /** 每页数量 */
  limit?: number
}

/**
 * 消息列表分页查询 Hook
 *
 * @param sessionId 会话 ID
 * @param options 查询选项
 * @returns 分页查询结果和扁平化的消息列表
 */
export const useInfiniteMessages = (
  sessionId: string,
  options: UseInfiniteMessagesOptions = {}
) => {
  const { enabled = true, limit = DEFAULT_LIMIT } = options

  const query = useInfiniteQuery({
    ...messagesQueryOptions(sessionId, { limit }),
    enabled: !!sessionId && enabled,
  })

  // 展平消息列表并反转顺序
  // API 返回 desc 顺序（从新到旧），需要反转为 asc 顺序（从旧到新）用于 UI 显示
  const flatMessages = useMemo(() => {
    const messages = query.data?.pages.flatMap((p) => p?.messages ?? []) ?? []
    // 反转消息列表，使其从旧到新排序
    return [...messages].reverse()
  }, [query.data])

  // 获取首页的 session 信息
  const sessionInfo = useMemo(() => {
    return query.data?.pages[0]?.session
  }, [query.data])

  // 是否有更多历史消息
  const hasMore = useMemo(() => {
    const pages = query.data?.pages
    if (!pages || pages.length === 0) return false
    return pages[pages.length - 1]?.hasMore ?? false
  }, [query.data])

  return {
    ...query,
    flatMessages,
    sessionInfo,
    hasMore,
  }
}

/**
 * 清除消息列表缓存
 *
 * 使指定 session 的消息列表缓存失效，下次查询时将重新从服务器获取最新数据。
 * 适用场景：刷新、预览、或任何需要确保消息列表数据最新的操作。
 *
 * @param queryClient - React Query 的 QueryClient 实例
 * @param sessionId - 会话 ID
 */
export const clearMessagesCache = (
  queryClient: QueryClient,
  sessionId: string
): void => {
  logger.debug('[clearMessagesCache] 清除消息缓存', { sessionId })

  queryClient.invalidateQueries({
    queryKey: ['messages', sessionId],
  })
}
